import { useState, useRef, useEffect, useCallback } from "react";
import Avvvatars from "avvvatars-react";
import Icon1 from "@assets/icons/Icon";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { useMessagerieStore } from "@store/messagerieStore";
import type { ChatMessage as StoreChatMessage, DiscussionTab } from "types/messaging";

// ─── Types ──────────────────────────────────────────────────

const EMOJI_LIST = ["👍", "❤️", "😂", "🔥", "😍", "👏", "😮", "😢", "🙏", "💯", "✅", "🎉"];

/** Display-ready message used internally by ChatPanel */
interface DisplayMessage {
  id: string;
  senderName: string;
  senderRole: string;
  text?: string;
  timestamp: string;
  isOwn: boolean;
  attachment?: { name: string; type: "pdf" | "image" | "file"; url?: string };
  reactions: { emoji: string; userId: string }[];
  replyToId?: string;
  deleted?: boolean;
  channel?: string | null;
}

export interface ChatMember {
  name: string;
}

// ─── Adapter ────────────────────────────────────────────────

function toDisplayMessage(msg: StoreChatMessage, currentUserId: string, members: ReturnType<typeof useMessagerieStore.getState>["members"]): DisplayMessage {
  const senderId = msg.sender_id || msg.senderId || "";
  const member = members.find((m) => m.id === senderId);
  return {
    id: msg.id,
    senderName: msg.sender_name || member?.name || "Inconnu",
    senderRole: member?.role || "",
    text: msg.text,
    timestamp: msg.timestamp || "",
    isOwn: senderId === currentUserId,
    attachment: msg.attachment ? { name: msg.attachment.name || "Fichier", type: msg.attachment.type === "image" ? "image" : "file", url: msg.attachment.url } : undefined,
    reactions: (msg.reactions || []).map((r) => ({ emoji: r.emoji, userId: r.user_id || r.userId || "" })),
    replyToId: msg.reply_to_id || msg.replyToId,
    deleted: msg.deleted,
    channel: msg.channel,
  };
}

// ─── Helpers ────────────────────────────────────────────────

function groupChatMessages(messages: DisplayMessage[]) {
  const groups: { senderId: string; isOwn: boolean; messages: DisplayMessage[] }[] = [];
  for (const msg of messages) {
    if (msg.deleted) {
      groups.push({ senderId: msg.senderName, isOwn: msg.isOwn, messages: [msg] });
      continue;
    }
    const last = groups[groups.length - 1];
    if (last && last.isOwn === msg.isOwn && last.senderId === msg.senderName && !last.messages[last.messages.length - 1].deleted) {
      last.messages.push(msg);
    } else {
      groups.push({ senderId: msg.senderName, isOwn: msg.isOwn, messages: [msg] });
    }
  }
  return groups;
}

function getBubbleRadius(isOwn: boolean, position: "solo" | "first" | "middle" | "last"): string {
  if (position === "solo") return isOwn ? "10px 10px 4px 10px" : "10px 10px 10px 4px";
  if (isOwn) {
    if (position === "first") return "10px 10px 4px 10px";
    if (position === "middle") return "10px 4px 4px 10px";
    return "10px 4px 10px 10px";
  }
  if (position === "first") return "10px 10px 10px 4px";
  if (position === "middle") return "4px 10px 10px 4px";
  return "4px 10px 10px 10px";
}

// ─── Sub-components ─────────────────────────────────────────

function ChatEmojiPicker({
  onSelect,
  onClose,
  alignRight,
}: {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  alignRight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        bottom: "100%",
        ...(alignRight ? { right: 0 } : { left: 0 }),
        marginBottom: 4,
        padding: 6,
        background: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: 10,
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 2,
      }}
    >
      {EMOJI_LIST.map((emoji) => (
        <button
          key={emoji}
          onClick={() => onSelect(emoji)}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 32, height: 32, fontSize: 18 }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function ReactionPill({
  emoji,
  count,
  isMine,
  onToggle,
}: {
  emoji: string;
  count: number;
  isMine: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center justify-center"
        style={{ height: 20, padding: 0, gap: 1, background: "transparent", border: "none" }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>
        {count > 1 && (
          <span
            className="text-h5"
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#999999" }}
          >
            {count}
          </span>
        )}
      </button>
    </div>
  );
}

function ChatBubble({
  msg,
  position,
  showTimestamp,
  replySource,
  onReact,
  onReply,
  onDelete,
  onPreview,
  isDark,
}: {
  msg: DisplayMessage;
  position: "solo" | "first" | "middle" | "last";
  showTimestamp: boolean;
  replySource?: DisplayMessage;
  onReact: (msgId: string, emoji: string) => void;
  onReply: (msgId: string) => void;
  onDelete: (msgId: string) => void;
  onPreview: (attachment: { name: string; type: string; url: string }) => void;
  isDark?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const bubbleBg = isDark
    ? (msg.isOwn ? "#525252" : "#404040")
    : (msg.isOwn ? "#000000" : "#F5F5F5");
  const radius = getBubbleRadius(msg.isOwn, position);

  const grouped = msg.reactions.reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = { count: 0, isMine: false };
    acc[r.emoji].count++;
    if (r.userId === "me") acc[r.emoji].isMine = true;
    return acc;
  }, {} as Record<string, { count: number; isMine: boolean }>);
  const hasReactions = Object.keys(grouped).length > 0;

  if (msg.deleted) {
    return (
      <div style={{ padding: "8px 12px", background: bubbleBg, borderRadius: radius, maxWidth: "100%", opacity: 0.7 }}>
        <span className="text-h2 italic" style={{ fontFamily: "Poppins", color: isDark ? "#6B7280" : "#a3a3a3" }}>Ce message a été supprimé</span>
      </div>
    );
  }

  return (
    <div
      className="relative"
      style={{ minWidth: 80, width: "fit-content", paddingBottom: hasReactions ? 14 : 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!showEmojiPicker) setHovered(false); }}
    >
      {(hovered || showEmojiPicker) && (
        <div
          className="absolute flex flex-row items-center z-10"
          style={{
            top: -8,
            ...(msg.isOwn ? { left: -8 } : { right: -8 }),
            gap: 2,
            padding: "2px 4px",
            background: isDark ? "#404040" : "#FFFFFF",
            boxShadow: isDark ? "0px 2px 6px rgba(0, 0, 0, 0.4)" : "0px 2px 6px rgba(0, 0, 0, 0.12)",
            borderRadius: 8,
          }}
        >
          <div className="relative">
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"} rounded-md flex items-center justify-center`}
              style={{ width: 28, height: 28 }}
            >
              <span style={{ fontSize: 14 }}>😀</span>
            </button>
            {showEmojiPicker && (
              <ChatEmojiPicker
                alignRight={msg.isOwn}
                onSelect={(emoji) => { onReact(msg.id, emoji); setShowEmojiPicker(false); setHovered(false); }}
                onClose={() => { setShowEmojiPicker(false); setHovered(false); }}
              />
            )}
          </div>
          <button
            onClick={() => onReply(msg.id)}
            className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"} rounded-md flex items-center justify-center`}
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="response" size={14} color="#A1A1A1" />
          </button>
          {msg.isOwn && (
            <button
              onClick={() => onDelete(msg.id)}
              className={`${isDark ? "hover:bg-red-900/30" : "hover:bg-red-50"} rounded-md flex items-center justify-center`}
              style={{ width: 28, height: 28 }}
            >
              <Icon1 name="delete" size={14} color="#ef4444" />
            </button>
          )}
        </div>
      )}

      <div style={{ background: bubbleBg, borderRadius: radius, boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)", overflow: "hidden" }}>
        {replySource && (
          <div
            style={{
              margin: "6px 8px 0",
              padding: "6px 10px",
              background: isDark
                ? (msg.isOwn ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)")
                : (msg.isOwn ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.03)"),
              borderRadius: 8,
              borderLeft: `3px solid ${isDark ? "#D4D4D4" : "#525252"}`,
              maxWidth: 280,
            }}
          >
            <div className="text-h4 font-semibold" style={{ fontFamily: "Poppins", color: isDark ? "#D4D4D4" : "#525252", marginBottom: 2 }}>
              {replySource.senderName}
            </div>
            <div
              className={isDark ? "text-h4" : "text-h4 text-neutral-400"}
              style={{ fontFamily: "Poppins", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(isDark ? { color: "#6B7280" } : {}) }}
            >
              {replySource.deleted ? "Ce message a été supprimé" : replySource.text || "📎 Pièce jointe"}
            </div>
          </div>
        )}

        {msg.text && (
          <div style={{ padding: "8px 12px 6px" }}>
            <span
              className="text-h2"
              style={{ fontFamily: "Poppins", fontWeight: 400, lineHeight: "20px", color: isDark ? (msg.isOwn ? "#FFFFFF" : "#E5E5E5") : (msg.isOwn ? "#FFFFFF" : "#000000"), whiteSpace: "pre-wrap" }}
            >
              {msg.text}
            </span>
          </div>
        )}

        {msg.attachment && msg.attachment.type === "image" && msg.attachment.url && (
          <div
            style={{ padding: "4px", cursor: "pointer" }}
            onClick={() => onPreview({ name: msg.attachment!.name, type: "image", url: msg.attachment!.url! })}
          >
            <img
              src={msg.attachment.url}
              alt={msg.attachment.name}
              style={{ maxWidth: 260, maxHeight: 200, objectFit: "cover", borderRadius: 8 }}
            />
          </div>
        )}
        {msg.attachment && msg.attachment.type !== "image" && (
          <div
            className="flex flex-row items-center cursor-pointer"
            style={{ padding: "8px 12px", gap: 10 }}
            onClick={() => msg.attachment?.url && onPreview({ name: msg.attachment.name, type: msg.attachment.type, url: msg.attachment.url })}
          >
            <Icon1 name="file" size={16} color={isDark ? (msg.isOwn ? "#D4D4D4" : "#E5E5E5") : (msg.isOwn ? "#FFFFFF" : "#000000")} />
            <span className="text-h2" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? (msg.isOwn ? "#FFFFFF" : "#E5E5E5") : (msg.isOwn ? "#FFFFFF" : "#000000"), flex: 1 }}>
              {msg.attachment.name}
            </span>
          </div>
        )}

        {showTimestamp && (
          <div className="flex justify-end items-center" style={{ padding: "0 12px 6px", gap: 4 }}>
            {msg.channel === "whatsapp" && (
              <span className="text-h5" style={{ fontFamily: "Poppins", fontWeight: 500, color: msg.isOwn ? "#25D366" : "#25D366", fontSize: 10 }}>
                via WhatsApp
              </span>
            )}
            <span className="text-h5" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? (msg.isOwn ? "#D4D4D4" : "#6B7280") : (msg.isOwn ? "#A3A3A3" : "#A1A1A1") }}>
              {msg.timestamp}
            </span>
          </div>
        )}
      </div>

      {hasReactions && (
        <div
          className="flex flex-row flex-wrap absolute"
          style={{ bottom: 0, ...(msg.isOwn ? { right: 8 } : { left: 8 }), gap: 2 }}
        >
          {Object.entries(grouped).map(([emoji, { count, isMine }]) => (
            <ReactionPill key={emoji} emoji={emoji} count={count} isMine={isMine} onToggle={() => onReact(msg.id, emoji)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Attachment Preview Modal ────────────────────────────────

function AttachmentPreviewModal({ name, url, onClose }: { name: string; url: string; onClose: () => void }) {
  const isImage = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i.test(name) || url.startsWith("blob:");
  const isPdf = /\.pdf$/i.test(name);

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const zoomIn = () => setZoom((z) => Math.min(z * 1.3, 8));
  const zoomOut = () => setZoom((z) => Math.max(z / 1.3, 0.1));
  const resetView = () => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); };
  const rotateCW = () => setRotation((r) => (r + 90) % 360);
  const rotateCCW = () => setRotation((r) => (r - 90 + 360) % 360);

  const handleWheel = (e: React.WheelEvent) => { e.preventDefault(); e.deltaY < 0 ? zoomIn() : zoomOut(); };
  const handleMouseDown = (e: React.MouseEvent) => { if (e.button !== 0) return; setDragging(true); dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }; };
  const handleMouseMove = (e: React.MouseEvent) => { if (!dragging) return; setPan({ x: dragStart.current.panX + (e.clientX - dragStart.current.x), y: dragStart.current.panY + (e.clientY - dragStart.current.y) }); };
  const handleMouseUp = () => setDragging(false);

  useEffect(() => {
    if (!isImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") resetView();
      else if (e.key === "r") rotateCW();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isImage]);

  useEffect(() => {
    if (isImage) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isImage]);

  const toolbarBtn = "flex items-center justify-center rounded-lg hover:bg-neutral-200 transition-colors";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "80vw", maxWidth: 900, height: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <span className="text-h2 font-semibold">{name}</span>
          <div className="flex flex-row items-center gap-2">
            <button
              onClick={() => window.open(url, "_blank")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-h3 font-medium text-neutral-600"
            >
              <Icon1 name="download" size={14} color="#525252" />
              Télécharger
            </button>
            <button onClick={onClose} className={toolbarBtn} style={{ width: 32, height: 32 }}>
              <Icon1 name="close" size={16} color="#525252" />
            </button>
          </div>
        </div>

        {/* Image toolbar */}
        {isImage && (
          <div className="flex flex-row items-center justify-center gap-1 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
            <button onClick={zoomOut} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Dézoomer (-)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <span className="text-h4 text-neutral-500 w-12 text-center select-none">{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Zoomer (+)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M8 4v8M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={rotateCCW} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Rotation gauche">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M2 6.5A5.5 5.5 0 0 1 12.95 5M2 2.5V6.5H6" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <button onClick={rotateCW} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Rotation droite (R)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M14 6.5A5.5 5.5 0 0 0 3.05 5M14 2.5V6.5H10" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={resetView} className={toolbarBtn + " px-2 gap-1"} style={{ height: 32 }} title="Réinitialiser (0)">
              <svg width={14} height={14} viewBox="0 0 14 14" fill="none"><path d="M1 7a6 6 0 1 1 1.46 3.93" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" /><path d="M1 11V7.5H4.5" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span className="text-h4 text-neutral-500">1:1</span>
            </button>
          </div>
        )}

        {/* Content */}
        <div
          className="flex-1 overflow-hidden flex items-center justify-center bg-neutral-50"
          style={{ cursor: isImage ? (dragging ? "grabbing" : "grab") : "default" }}
          onWheel={isImage ? handleWheel : undefined}
          onMouseDown={isImage ? handleMouseDown : undefined}
          onMouseMove={isImage ? handleMouseMove : undefined}
          onMouseUp={isImage ? handleMouseUp : undefined}
          onMouseLeave={isImage ? handleMouseUp : undefined}
        >
          {isPdf ? (
            <iframe src={url} className="w-full h-full" style={{ border: "none" }} title={name} />
          ) : isImage ? (
            <img
              src={url}
              alt={name}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: dragging ? "none" : "transform 0.15s ease",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
              }}
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-neutral-400">
              <Icon1 name="file" size={48} color="#D4D4D4" />
              <span className="text-h2 font-medium">Aperçu non disponible</span>
              <button
                onClick={() => window.open(url, "_blank")}
                className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-h3 font-medium hover:bg-indigo-600"
              >
                Télécharger le fichier
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main ChatPanel ─────────────────────────────────────────

export interface ChatPanelProps {
  discussionId?: string;
  internalDiscussionId?: string;
  entityType?: "dossier" | "ordre";
  entityId?: string;
  entityName?: string;
  members?: ChatMember[];
  onlineCount?: number;
  compact?: boolean;
  whatsappLinked?: boolean;
  clientChannel?: "whatsapp" | "sms" | "email" | null;
}

export default function ChatPanel({
  discussionId,
  internalDiscussionId,
  entityType,
  entityId,
  entityName,
  members = [],
  onlineCount = 0,
  compact = false,
  whatsappLinked = false,
  clientChannel = null,
}: ChatPanelProps) {
  const [chatTab, setChatTab] = useState("client");
  const channelTab = clientChannel || "whatsapp";
  const [inputText, setInputText] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{ name: string; type: string; url: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDark = chatTab === "privee";
  const activeDiscussionId = chatTab === "client" ? discussionId : internalDiscussionId;

  // Check if discussion is linked to WhatsApp (from store or prop)
  const storeDiscussion = useMessagerieStore((s) => discussionId ? s.discussions.find((d) => d.id === discussionId) : undefined);
  const isWhatsappLinked = whatsappLinked || !!storeDiscussion?.whatsapp_phone;

  // Store state
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const storeMembers = useMessagerieStore((s) => s.members);
  const storeMessages = useMessagerieStore((s) => activeDiscussionId ? s.messages[activeDiscussionId] : undefined);
  const isLoadingMessages = useMessagerieStore((s) => s.isLoadingMessages);
  const fetchMessages = useMessagerieStore((s) => s.fetchMessages);
  const sendMessageTo = useMessagerieStore((s) => s.sendMessageTo);
  const deleteMessageIn = useMessagerieStore((s) => s.deleteMessageIn);
  const toggleReactionIn = useMessagerieStore((s) => s.toggleReactionIn);

  // Convert store messages to display messages
  const messages: DisplayMessage[] = (storeMessages || []).map((m) => toDisplayMessage(m, currentUserId, storeMembers));

  // Fetch messages when discussion changes
  useEffect(() => {
    if (activeDiscussionId) {
      fetchMessages(activeDiscussionId);
    }
  }, [activeDiscussionId, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    if (replyingToId) inputRef.current?.focus();
  }, [replyingToId]);

  useEffect(() => {
    if (!showAttachMenu) return;
    function handleClick(e: MouseEvent) {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) setShowAttachMenu(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showAttachMenu]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;
    if (!activeDiscussionId) return;

    // When on WhatsApp channel tab and discussion is linked, send via WhatsApp
    const channel = chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked ? "whatsapp" : undefined;
    sendMessageTo(activeDiscussionId, text, replyingToId, undefined, undefined, undefined, channel);
    setInputText("");
    setReplyingToId(null);
    if (inputRef.current) inputRef.current.style.height = "auto";
  }, [inputText, activeDiscussionId, replyingToId, sendMessageTo, chatTab, channelTab, isWhatsappLinked]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleReact = useCallback((msgId: string, emoji: string) => {
    if (!activeDiscussionId) return;
    toggleReactionIn(activeDiscussionId, msgId, emoji);
  }, [activeDiscussionId, toggleReactionIn]);

  const handleDelete = useCallback((msgId: string) => {
    if (!activeDiscussionId) return;
    deleteMessageIn(activeDiscussionId, msgId);
  }, [activeDiscussionId, deleteMessageIn]);

  const handleFileAttach = useCallback((file: File, type: "image" | "file") => {
    if (!activeDiscussionId) return;
    const attachment = { name: file.name, type } as const;
    const channel = chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked ? "whatsapp" : undefined;
    sendMessageTo(activeDiscussionId, "", replyingToId, attachment, undefined, file, channel);
    setReplyingToId(null);
    setShowAttachMenu(false);
  }, [activeDiscussionId, replyingToId, sendMessageTo, chatTab, channelTab, isWhatsappLinked]);

  const replyMessage = replyingToId ? messages.find((m) => m.id === replyingToId) : undefined;
  const groups = groupChatMessages(messages);

  const noDiscussion = !activeDiscussionId;

  return (
    <div className="flex flex-col h-full border-l" style={{ background: isDark ? "#1E1E2E" : "#FAFAFA", borderColor: isDark ? "#333348" : undefined }}>
      {/* Chat/Private tabs */}
      <div className="px-4 pt-4" style={{ background: isDark ? "#1E1E2E" : "#FFFFFF", borderBottom: `1px solid ${isDark ? "#333348" : "#E5E5E5"}` }}>
        <ChunkyTabs
          activeTab={chatTab}
          tabs={[{ value: "client", label: "Client" }, { value: "privee", label: "Interne" }]}
          setActiveTab={(tab: string) => { setChatTab(tab); setReplyingToId(null); }}
        />

        <div className="flex flex-row items-center justify-between pt-3 pb-3">
          {!isDark ? (
            <div
              className="flex flex-row items-center gap-1.5 px-3 py-1 rounded-full text-h4 font-medium border"
              style={{
                background: "#F0FDF4",
                borderColor: "#86EFAC",
                color: "#166534",
              }}
            >
              {channelTab === "whatsapp" && (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              )}
              {channelTab === "email" && <Icon1 name="email" size={14} color="#22c55e" />}
              {channelTab === "sms" && <Icon1 name="send" size={14} color="#22c55e" />}
              <span style={{ textTransform: "capitalize" }}>
                {channelTab === "sms" ? "Sms" : channelTab.charAt(0).toUpperCase() + channelTab.slice(1)}
              </span>
              {channelTab === "whatsapp" && isWhatsappLinked && (
                <div style={{ width: 6, height: 6, borderRadius: 999, background: "#25D366" }} />
              )}
            </div>
          ) : (
            <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Interne
            </span>
          )}

          {members.length > 0 && (
            <div className="flex flex-row items-center" style={{ gap: 8 }}>
              <div className="flex flex-row" style={{ marginLeft: -4 }}>
                {members.map((m, i) => (
                  <div key={m.name} className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999, marginLeft: i > 0 ? -8 : 0, border: isDark ? "2px solid #1E1E2E" : "2px solid #FFFFFF" }}>
                    <Avvvatars value={m.name} style="shape" size={24} />
                  </div>
                ))}
              </div>
              {onlineCount > 0 && (
                <div className="flex flex-row items-center" style={{ gap: 5 }}>
                  <div style={{ width: 6, height: 6, borderRadius: 999, background: "#5EEAD4" }} />
                  <span className="text-h3" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#5EEAD4" }}>
                    {onlineCount} en ligne
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto flex flex-col" style={{ gap: 16, padding: "16px 16px 8px", ...(compact ? { maxHeight: 400 } : {}) }}>
        {noDiscussion && (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-h3 text-neutral-400">Aucune conversation liée</span>
          </div>
        )}
        {!noDiscussion && isLoadingMessages && messages.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-h3 text-neutral-400">Chargement...</span>
          </div>
        )}
        {groups.map((group) => {
          const count = group.messages.length;
          return (
            <div
              key={group.messages[0].id}
              className="flex flex-row"
              style={{ justifyContent: group.isOwn ? "flex-end" : "flex-start", gap: 8 }}
            >
              {!group.isOwn && (
                <div className="flex-shrink-0" style={{ width: 32, paddingTop: 2 }}>
                  <div className="overflow-hidden" style={{ borderRadius: 999 }}>
                    <Avvvatars value={group.senderId} style="shape" size={32} />
                  </div>
                </div>
              )}

              <div className="flex flex-col" style={{ alignItems: group.isOwn ? "flex-end" : "flex-start", gap: 3, maxWidth: "75%" }}>
                {!group.isOwn && (
                  <div className="flex flex-row items-center" style={{ gap: 6, marginBottom: 2, marginLeft: 4 }}>
                    <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: isDark ? "#D4D4D4" : "#525252" }}>
                      {group.senderId}
                    </span>
                    {group.messages[0].channel === "whatsapp" && (
                      <span
                        className="flex items-center justify-center text-h5 font-medium"
                        style={{ background: "#25D366", color: "#FFFFFF", borderRadius: 4, padding: "1px 5px", fontSize: 10 }}
                      >
                        WhatsApp
                      </span>
                    )}
                    {group.messages[0].senderRole && (
                      <>
                        <div style={{ width: 4, height: 4, borderRadius: 999, background: "#D4D4D4" }} />
                        <span className="text-h4" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>{group.messages[0].senderRole}</span>
                      </>
                    )}
                  </div>
                )}

                {group.messages.map((msg, i) => {
                  const position: "solo" | "first" | "middle" | "last" =
                    count === 1 ? "solo" : i === 0 ? "first" : i === count - 1 ? "last" : "middle";
                  const showTimestamp = i === count - 1;
                  const replySource = msg.replyToId ? messages.find((m) => m.id === msg.replyToId) : undefined;

                  return (
                    <ChatBubble
                      key={msg.id}
                      msg={msg}
                      position={position}
                      showTimestamp={showTimestamp}
                      replySource={replySource}
                      onReact={handleReact}
                      onReply={setReplyingToId}
                      onDelete={handleDelete}
                      onPreview={setPreviewAttachment}
                      isDark={isDark}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "12px 16px 16px" }}>
        {replyMessage && (
          <div
            className="flex flex-row items-center justify-between"
            style={{
              padding: "8px 12px",
              marginBottom: 4,
              background: isDark ? "#404040" : "#F5F5F5",
              borderRadius: "10px 10px 0 0",
              borderLeft: `3px solid ${isDark ? "#D4D4D4" : "#525252"}`,
            }}
          >
            <div className="flex flex-col" style={{ gap: 2, overflow: "hidden", flex: 1 }}>
              <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: isDark ? "#D4D4D4" : "#525252" }}>
                {replyMessage.senderName}
              </span>
              <span
                className={isDark ? "text-h4" : "text-h4 text-neutral-400"}
                style={{ fontFamily: "Poppins", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(isDark ? { color: "#6B7280" } : {}) }}
              >
                {replyMessage.text || "📎 Pièce jointe"}
              </span>
            </div>
            <button
              onClick={() => setReplyingToId(null)}
              className={`flex-shrink-0 ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-full flex items-center justify-center`}
              style={{ width: 24, height: 24 }}
            >
              <Icon1 name="close" size={10} color="#A1A1A1" />
            </button>
          </div>
        )}

        <div
          className="flex flex-row items-center relative"
          style={{ padding: "8px 10px", gap: 10, background: isDark ? "#404040" : "#F5F5F5", borderRadius: replyMessage ? "0 0 10px 10px" : 10 }}
        >
          <div className="relative">
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
              style={{ width: 28, height: 28 }}
            >
              <Icon1 name="plus" size={12} color={isDark ? "#D4D4D4" : "#A1A1A1"} />
            </button>
            {showAttachMenu && (
              <div
                ref={attachMenuRef}
                className="absolute z-50 flex flex-col"
                style={{
                  bottom: "100%", left: 0, marginBottom: 8,
                  background: isDark ? "#404040" : "#FFFFFF",
                  boxShadow: isDark ? "0px 4px 12px rgba(0, 0, 0, 0.4)" : "0px 4px 12px rgba(0, 0, 0, 0.15)",
                  borderRadius: 10, overflow: "hidden", minWidth: 160,
                }}
              >
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className={`flex flex-row items-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-50"} text-h2`}
                  style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#000000" }}
                >
                  <Icon1 name="image" size={16} color="#A1A1A1" />
                  Image
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex flex-row items-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-50"} text-h2`}
                  style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#000000" }}
                >
                  <Icon1 name="join" size={16} color="#A1A1A1" />
                  Fichier
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileAttach(f, "image"); e.target.value = ""; }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileAttach(f, "file"); e.target.value = ""; }}
                />
              </div>
            )}
          </div>

          <textarea
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={isDark ? "Message interne..." : (chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked ? "Envoyer via WhatsApp..." : "Entrez un message")}
            rows={1}
            disabled={noDiscussion}
            className={`flex-1 outline-none border-none text-h2 resize-none ${isDark ? "placeholder:text-[#6B7280]" : ""}`}
            style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#FFFFFF" : "#000000", background: "transparent", maxHeight: 120, overflowY: "auto" }}
          />

          <button
            onClick={handleSend}
            disabled={noDiscussion}
            className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="send" size={16} color={inputText.trim() ? (isDark ? "#D4D4D4" : "#525252") : "#A1A1A1"} />
          </button>
        </div>
      </div>

      {previewAttachment && (
        <AttachmentPreviewModal
          name={previewAttachment.name}
          url={previewAttachment.url}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
}
